import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { contactFormApi } from "api/index";
import { StatusScreen, StatusScreenProvider, useStatusScreenContext } from "components/UI/StatusScreen";
import { COUNTRIES_LIST, COUNTRY_DEFAULT_VALUES } from "constants/countries.constans";
import { getDefaultFormValues } from "constants/formDefaults.constans";
import { Country } from "types/country.types";
import type { FormValues } from "types/form.types";
import { getCountryFromStoredUser, getStoredUser, updateStoredUserCountryCode } from "utils/user.utils";
import { ContactForm } from "./components/ContactForm";
import { ContactInfo } from "./components/ContactInfo";
import styles from "./MainViews.module.scss";

const MainViewsContent = () => {
  const [userCountry, setUserCountry] = useState<Country>(() => getCountryFromStoredUser() ?? COUNTRY_DEFAULT_VALUES);
  const [isCountryLoading, setIsCountryLoading] = useState(() => !getCountryFromStoredUser());
  const { statusResponse, isStatusScreenOpen, setIsStatusScreenOpen } = useStatusScreenContext();

  const methods = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: getDefaultFormValues(),
  });

  useEffect(() => {
    const resolveUserCountry = async () => {
      const storedUser = getStoredUser();
      const countryFromUser = getCountryFromStoredUser();

      if (countryFromUser) {
        setUserCountry(countryFromUser);
        return;
      }

      setIsCountryLoading(true);

      try {
        const countryInfo = await contactFormApi.getCountry();
        const countryCodeFromApi = countryInfo?.data.country?.toUpperCase() || COUNTRY_DEFAULT_VALUES.countryCode;

        const matchedCountry =
          COUNTRIES_LIST.find((country) => country.countryCode.toUpperCase() === countryCodeFromApi) ||
          COUNTRY_DEFAULT_VALUES;

        setUserCountry(matchedCountry);

        if (storedUser?.data && !storedUser.data.country_code) {
          updateStoredUserCountryCode(matchedCountry.countryCode);
        }
      } finally {
        setIsCountryLoading(false);
      }
    };

    resolveUserCountry();
  }, []);

  if (isStatusScreenOpen) {
    return (
      <StatusScreen
        title={statusResponse === "success" ? "Thanks!" : "Request Failed"}
        description={
          statusResponse === "success"
            ? "We’ll work on your inquiry and give a feedback within 24 hours."
            : "Your request was not submitted successfully. Please try again later or contact support if the issue persists."
        }
        onClose={() => setIsStatusScreenOpen(false)}
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <div className={styles.mainViews}>
        <ContactForm isCountryLoading={isCountryLoading} userCountry={userCountry} />
        <ContactInfo />
      </div>
    </FormProvider>
  );
};

export const MainViews = () => {
  return (
    <StatusScreenProvider>
      <MainViewsContent />
    </StatusScreenProvider>
  );
};
